import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { Sale, SaleItem, Location } from '../types';

interface ReceiptProps {
  sale: Partial<Sale> & {
    items: SaleItem[];
    totalAmount?: number;
    subtotalAmount?: number;
    total?: number; // Backend field name
    subtotal?: number; // Backend field name
    taxAmount?: number;
    discountAmount?: number;
    paymentMethod?: string;
    createdAt?: string;
    localId?: string; // For offline sales
  };
  organization?: { name?: string };
  location?: Location;
}

/**
 * Receipt component for printing sales receipts
 * Designed to be rendered in a print dialog or window
 */
export const Receipt: React.FC<ReceiptProps> = ({ sale, organization, location }) => {
  const formatCurrency = (amount: number | undefined) => {
    return amount !== undefined ? `$${amount.toFixed(2)}` : '$0.00';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return new Date().toLocaleString();
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box
      sx={{
        padding: 2,
        fontFamily: 'monospace',
        maxWidth: '80mm',
        margin: '0 auto',
        backgroundColor: 'white',
        color: 'black',
        '@media print': {
          padding: 1,
        },
      }}
    >
      {/* Header */}
      <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', mb: 1 }}>
        {organization?.name || 'Store Receipt'}
      </Typography>
      {location?.address && (
        <Typography variant="body2" align="center" sx={{ mb: 1 }}>
          {location.address}
        </Typography>
      )}

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* Sale Info */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2">Date: {formatDate(sale.createdAt)}</Typography>
        <Typography variant="body2">
          Sale #: {sale.id || sale.localId || 'N/A'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* Items */}
      <Box sx={{ mb: 1 }}>
        {sale.items.map((item, index) => {
          const itemPrice = item.unitPrice ?? item.price ?? 0;
          return (
            <Box key={index} sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  {item.productName || item.product?.name || 'Product'} x{item.quantity}
                </Typography>
                <Typography variant="body2">{formatCurrency(itemPrice * item.quantity)}</Typography>
              </Box>
              {item.discount && item.discount > 0 && (
                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                  Discount: -{formatCurrency(item.discount)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* Totals */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Subtotal:</Typography>
          <Typography variant="body2">{formatCurrency(sale.subtotal ?? sale.subtotalAmount)}</Typography>
        </Box>
        {sale.taxAmount !== undefined && sale.taxAmount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Tax:</Typography>
            <Typography variant="body2">{formatCurrency(sale.taxAmount)}</Typography>
          </Box>
        )}
        {sale.discountAmount !== undefined && sale.discountAmount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Discount:</Typography>
            <Typography variant="body2">-{formatCurrency(sale.discountAmount)}</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Total */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          TOTAL:
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {formatCurrency(sale.total ?? sale.totalAmount)}
        </Typography>
      </Box>

      {/* Payment Method */}
      {sale.paymentMethod && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            Payment: {sale.paymentMethod.toUpperCase()}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* Footer */}
      <Typography variant="caption" align="center" sx={{ display: 'block', mt: 2 }}>
        Thank you for your business!
      </Typography>
    </Box>
  );
};

export default Receipt;
